import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Plus, Minus, Timer, X, UserMinus } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CardsPanel = ({ 
  game, 
  syncing, 
  onCard,
  gameId,
  getAuthHeaders,
  currentGameTime
}) => {
  const isGAA = game.sport === 'gaelic_football' || game.sport === 'hurling';
  const isRugby = game.sport === 'rugby';
  
  const [showSinBinDialog, setShowSinBinDialog] = useState(false);
  const [sinBinTeam, setSinBinTeam] = useState(1);
  const [sinBinPlayer, setSinBinPlayer] = useState('');
  const [sinBinNumber, setSinBinNumber] = useState('');
  const [sinBinDuration, setSinBinDuration] = useState(10);
  const [sinBinEntries, setSinBinEntries] = useState([]);
  // Use parent-provided server-authoritative game time; fall back to game.current_time
  const localTime = currentGameTime != null ? currentGameTime : (game.current_time || 0);
  
  // Fetch sin bin data
  useEffect(() => {
    if (isRugby && gameId && getAuthHeaders) {
      fetchSinBin();
    }
  }, [isRugby, gameId, game.sin_bin]);
  
  const fetchSinBin = async () => {
    try {
      const response = await axios.get(`${API}/games/${gameId}/sin-bin`, {
        headers: getAuthHeaders()
      });
      setSinBinEntries(response.data.sin_bin || []);
    } catch (error) {
      console.error('Failed to fetch sin bin:', error);
      setSinBinEntries(game.sin_bin || []);
    }
  };
  
  const handleAddToSinBin = async () => {
    if (!sinBinPlayer.trim()) return;
    
    try {
      await axios.post(
        `${API}/games/${gameId}/sin-bin?team=${sinBinTeam}&player_name=${encodeURIComponent(sinBinPlayer)}&player_number=${encodeURIComponent(sinBinNumber)}&duration=${sinBinDuration}`,
        null,
        { headers: getAuthHeaders() }
      );
      setShowSinBinDialog(false);
      setSinBinPlayer('');
      setSinBinNumber('');
      fetchSinBin();
    } catch (error) {
      console.error('Failed to add to sin bin:', error);
    }
  };
  
  const handleRemoveFromSinBin = async (entryId) => {
    try {
      await axios.delete(`${API}/games/${gameId}/sin-bin/${entryId}`, {
        headers: getAuthHeaders()
      });
      fetchSinBin();
    } catch (error) {
      console.error('Failed to remove from sin bin:', error);
    }
  };
  
  const formatRemaining = (entry) => {
    const remaining = Math.max(0, (entry.end_time || 0) - localTime);
    const mins = Math.floor(remaining / 60);
    const secs = remaining % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const openSinBinDialog = (teamNum) => {
    setSinBinTeam(teamNum);
    setSinBinPlayer('');
    setSinBinNumber('');
    setSinBinDuration(10);
    setShowSinBinDialog(true);
  };
  
  const CardControls = ({ teamNum, team }) => {
    const teamSinBin = sinBinEntries.filter(e => e.team === teamNum && (e.end_time > localTime));
    
    return (
      <Card className="bg-[#1E293B] border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-white font-heading text-xl flex items-center justify-between">
            <span>{team.name}</span>
            {isRugby && teamSinBin.length > 0 && (
              <span className="text-yellow-400 text-sm font-body flex items-center gap-1">
                <Timer className="w-4 h-4" />
                {teamSinBin.length} in sin bin
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`grid ${isGAA ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}>
            {/* Yellow Cards */}
            <div className="text-center">
              <p className="text-yellow-400 font-body text-xs mb-2">Yellow</p>
              <div className="flex items-center justify-center gap-1">
                <Button
                  data-testid={`team${teamNum}-yellow-minus`}
                  onClick={() => onCard(teamNum, 'yellow', 'remove')}
                  disabled={syncing}
                  className="h-10 w-10 bg-yellow-600 hover:bg-yellow-700 text-white touch-target"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="font-mono text-2xl text-yellow-400 w-8">{team.yellow_cards || 0}</span>
                <Button
                  data-testid={`team${teamNum}-yellow-plus`}
                  onClick={() => onCard(teamNum, 'yellow', 'add')}
                  disabled={syncing}
                  className="h-10 w-10 bg-yellow-600 hover:bg-yellow-700 text-white touch-target"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Black Cards (GAA only) */}
            {isGAA && (
              <div className="text-center">
                <p className="text-slate-300 font-body text-xs mb-2">Black</p>
                <div className="flex items-center justify-center gap-1">
                  <Button
                    data-testid={`team${teamNum}-black-minus`}
                    onClick={() => onCard(teamNum, 'black', 'remove')}
                    disabled={syncing}
                    className="h-10 w-10 bg-slate-800 hover:bg-slate-700 text-white border border-slate-600 touch-target"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="font-mono text-2xl text-slate-300 w-8">{team.black_cards || 0}</span>
                  <Button
                    data-testid={`team${teamNum}-black-plus`}
                    onClick={() => onCard(teamNum, 'black', 'add')}
                    disabled={syncing}
                    className="h-10 w-10 bg-slate-800 hover:bg-slate-700 text-white border border-slate-600 touch-target"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
            
            {/* Red Cards */}
            <div className="text-center">
              <p className="text-red-400 font-body text-xs mb-2">Red</p>
              <div className="flex items-center justify-center gap-1">
                <Button
                  data-testid={`team${teamNum}-red-minus`}
                  onClick={() => onCard(teamNum, 'red', 'remove')}
                  disabled={syncing}
                  className="h-10 w-10 bg-red-600 hover:bg-red-700 text-white touch-target"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="font-mono text-2xl text-red-400 w-8">{team.red_cards || 0}</span>
                <Button
                  data-testid={`team${teamNum}-red-plus`}
                  onClick={() => onCard(teamNum, 'red', 'add')}
                  disabled={syncing}
                  className="h-10 w-10 bg-red-600 hover:bg-red-700 text-white touch-target"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          
          {/* Sin Bin Section (Rugby only) */}
          {isRugby && (
            <div className="mt-4 pt-3 border-t border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <p className="text-yellow-400 font-body text-sm flex items-center gap-1">
                  <Timer className="w-4 h-4" />
                  Sin Bin
                </p>
                <Button
                  data-testid={`team${teamNum}-sinbin-add`}
                  onClick={() => openSinBinDialog(teamNum)}
                  size="sm"
                  className="h-7 px-2 bg-yellow-600 hover:bg-yellow-700 text-white text-xs"
                >
                  <UserMinus className="w-3 h-3 mr-1" />
                  Add Player
                </Button>
              </div>
              
              {teamSinBin.length > 0 ? (
                <div className="space-y-2">
                  {teamSinBin.map((entry) => (
                    <div key={entry.id} className="flex items-center justify-between bg-yellow-900/30 rounded px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-yellow-400 font-mono text-lg font-bold">
                          {formatRemaining(entry)}
                        </span>
                        <span className="text-white text-sm">
                          {entry.player_number && `#${entry.player_number} `}
                          {entry.player_name}
                        </span>
                      </div>
                      <Button
                        onClick={() => handleRemoveFromSinBin(entry.id)}
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 text-slate-400 hover:text-white hover:bg-slate-700"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-xs">No players in sin bin</p>
              )}
            </div>
          )}
          
          <p className="text-slate-500 text-xs mt-3 text-center">
            {isGAA 
              ? 'Black = sent off (replaceable) • Red = sent off (no replacement)'
              : isRugby
                ? 'Yellow = 10-min sin bin • Red = permanent dismissal'
                : 'Red card triggers automatic "Playing with X men" overlay'
            }
          </p>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <div className="space-y-4">
        <CardControls teamNum={1} team={game.team1} />
        <CardControls teamNum={2} team={game.team2} />
      </div>
      
      {/* Sin Bin Dialog */}
      <Dialog open={showSinBinDialog} onOpenChange={setShowSinBinDialog}>
        <DialogContent className="bg-[#1E293B] border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Timer className="w-5 h-5 text-yellow-400" />
              Add Player to Sin Bin - {sinBinTeam === 1 ? game.team1?.name : game.team2?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-slate-300 text-sm font-body block mb-2">Player Name *</label>
              <Input
                value={sinBinPlayer}
                onChange={(e) => setSinBinPlayer(e.target.value)}
                placeholder="Enter player name..."
                className="bg-[#0F172A] border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-slate-300 text-sm font-body block mb-2">Jersey Number</label>
                <Input
                  value={sinBinNumber}
                  onChange={(e) => setSinBinNumber(e.target.value)}
                  placeholder="e.g. 7"
                  className="bg-[#0F172A] border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>
              <div>
                <label className="text-slate-300 text-sm font-body block mb-2">Duration (mins)</label>
                <Input
                  type="number"
                  value={sinBinDuration}
                  onChange={(e) => setSinBinDuration(parseInt(e.target.value) || 10)}
                  min="1"
                  max="20"
                  className="bg-[#0F172A] border-slate-600 text-white"
                />
              </div>
            </div>
            
            <div className="bg-yellow-900/30 rounded-lg p-3">
              <p className="text-yellow-400 text-sm">
                Player will return at: <strong>{Math.floor((localTime + sinBinDuration * 60) / 60)}:{((localTime + sinBinDuration * 60) % 60).toString().padStart(2, '0')}</strong>
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={() => setShowSinBinDialog(false)}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddToSinBin}
                disabled={!sinBinPlayer.trim()}
                className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white"
              >
                <Timer className="w-4 h-4 mr-2" />
                Start Sin Bin Timer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CardsPanel;
